import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { getCitoQuestions, getQuestionWithExplanation } from '../../data/questionLoader';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { LatexRenderer } from '../ui/LatexRenderer';
import { AnswerComparison } from '../practice/feedback/AnswerComparison';
import { ExplanationCard } from '../practice/feedback/ExplanationCard';
import { Clock, CheckCircle, XCircle, ArrowRight, Trophy, Target, Zap, AlertCircle, BarChart3, Timer, ChevronDown, ChevronUp, Brain, TrendingUp, Award, BookOpen, Sparkles, Heart, Star, Smile, Code, Rocket } from 'lucide-react';
import { formatSubSkill } from '../../utils/formatters';
interface CitoSimulatorProps {
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number) => void;
}
interface Answer {
  questionId: string;
  userChoice: number;
  isCorrect: boolean;
  timeSpent: number;
  difficulty: number;
  module: string;
  skill?: string;
  subSkill?: string;
}
type SimulatorView = 'intro' | 'test' | 'review';
export function CitoSimulator({
  onExit,
  onAnswer
}: CitoSimulatorProps) {
  const [view, setView] = useState<SimulatorView>('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(45 * 60);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isContextExpanded, setIsContextExpanded] = useState(true);
  const [citoQuestions] = useState(() => getCitoQuestions(30));
  const currentQuestion = citoQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === citoQuestions.length - 1;
  // Keyboard navigation
  useEffect(() => {
    if (view !== 'test') return;
    const handleKeyPress = (e: KeyboardEvent) => {
      // Number keys 1-4 for answers A-D
      if (e.key >= '1' && e.key <= '4') {
        const index = parseInt(e.key) - 1;
        if (index < currentQuestion.options.length) {
          handleAnswerSelect(index);
        }
      }
      // A-D keys for answers
      else if (e.key.toLowerCase() >= 'a' && e.key.toLowerCase() <= 'd') {
        const index = e.key.toLowerCase().charCodeAt(0) - 97;
        if (index < currentQuestion.options.length) {
          handleAnswerSelect(index);
        }
      }
      // Enter to submit
      else if (e.key === 'Enter' && selectedAnswer !== null) {
        handleNextQuestion();
      }
      // Ctrl+D to toggle debug overlay
      else if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setShowDebugInfo(!showDebugInfo);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [view, selectedAnswer, currentQuestion, showDebugInfo]);
  useEffect(() => {
    if (view !== 'test') return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleFinishTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [view]);
  useEffect(() => {
    if (view === 'test') {
      setQuestionStartTime(Date.now());
      setSelectedAnswer(null);
      setIsContextExpanded(true);
    }
  }, [currentQuestionIndex, view]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const getTimeStatus = () => {
    const elapsed = 45 * 60 - timeRemaining;
    const expectedTime = (currentQuestionIndex + 1) / citoQuestions.length * 45 * 60;
    if (elapsed < expectedTime * 0.8) return 'ahead';
    if (elapsed < expectedTime * 1.2) return 'ontrack';
    return 'behind';
  };
  const handleStartTest = () => {
    setView('test');
    setTimeRemaining(45 * 60);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setQuestionStartTime(Date.now());
  };
  const handleAnswerSelect = (choiceIndex: number) => {
    setSelectedAnswer(choiceIndex);
  };
  const handleNextQuestion = () => {
    if (selectedAnswer === null) return;
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newAnswer: Answer = {
      questionId: currentQuestion.id,
      userChoice: selectedAnswer,
      isCorrect,
      timeSpent,
      difficulty: currentQuestion.difficulty || 1,
      module: currentQuestion.module,
      skill: currentQuestion.skill,
      subSkill: currentQuestion.subSkill
    };
    setAnswers([...answers, newAnswer]);
    onAnswer(currentQuestion.id, isCorrect, selectedAnswer);
    if (isLastQuestion) {
      handleFinishTest();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
    }
  };
  const handleFinishTest = () => {
    setView('review');
  };
  const stats = useMemo(() => {
    if (answers.length === 0) return null;
    const correctCount = answers.filter(a => a.isCorrect).length;
    const incorrectCount = answers.length - correctCount;
    const percentage = Math.round(correctCount / answers.length * 100);
    const totalTimeSpent = answers.reduce((acc, curr) => acc + curr.timeSpent, 0);
    const avgTimePerQuestion = Math.round(totalTimeSpent / answers.length);
    const difficultyStats = {
      1: {
        total: 0,
        correct: 0
      },
      2: {
        total: 0,
        correct: 0
      },
      3: {
        total: 0,
        correct: 0
      }
    };
    answers.forEach(a => {
      const diff = a.difficulty as 1 | 2 | 3;
      if (difficultyStats[diff]) {
        difficultyStats[diff].total++;
        if (a.isCorrect) difficultyStats[diff].correct++;
      }
    });
    const skillStats = new Map<string, {
      total: number;
      correct: number;
    }>();
    const subSkillStats = new Map<string, {
      total: number;
      correct: number;
    }>();
    answers.forEach(a => {
      if (a.skill) {
        const skillData = skillStats.get(a.skill) || {
          total: 0,
          correct: 0
        };
        skillData.total++;
        if (a.isCorrect) skillData.correct++;
        skillStats.set(a.skill, skillData);
      }
      if (a.subSkill) {
        const subSkillData = subSkillStats.get(a.subSkill) || {
          total: 0,
          correct: 0
        };
        subSkillData.total++;
        if (a.isCorrect) subSkillData.correct++;
        subSkillStats.set(a.subSkill, subSkillData);
      }
    });
    const strengths: string[] = [];
    const weaknesses: string[] = [];
    subSkillStats.forEach((data, subSkill) => {
      const percentage = data.correct / data.total * 100;
      if (data.total >= 2) {
        if (percentage >= 80) {
          strengths.push(subSkill);
        } else if (percentage < 60) {
          weaknesses.push(subSkill);
        }
      }
    });
    let grade = 'III';
    let gradeColor = 'text-orange-600';
    let gradeText = 'Goed bezig';
    if (percentage >= 90) {
      grade = 'I+';
      gradeColor = 'text-emerald-600';
      gradeText = 'Fantastisch';
    } else if (percentage >= 80) {
      grade = 'I';
      gradeColor = 'text-emerald-500';
      gradeText = 'Super';
    } else if (percentage >= 70) {
      grade = 'II';
      gradeColor = 'text-blue-600';
      gradeText = 'Heel Goed';
    } else if (percentage >= 55) {
      grade = 'III';
      gradeColor = 'text-yellow-600';
      gradeText = 'Goed Bezig';
    } else if (percentage >= 40) {
      grade = 'IV';
      gradeColor = 'text-orange-600';
      gradeText = 'Op Weg';
    } else {
      grade = 'V';
      gradeColor = 'text-rose-600';
      gradeText = 'Nog Even Oefenen';
    }
    return {
      correctCount,
      incorrectCount,
      percentage,
      totalTimeSpent,
      avgTimePerQuestion,
      difficultyStats,
      skillStats,
      subSkillStats,
      strengths,
      weaknesses,
      grade,
      gradeColor,
      gradeText
    };
  }, [answers]);
  // Get module icon/emoji for debug overlay
  const getModuleIcon = (module: string) => {
    const icons: Record<string, string> = {
      Getalbegrip: '🔢',
      Metriek: '📏',
      Taalbegrip: '📖',
      Spellingregel: '✏️',
      SpellingWW: '📝',
      TijdGeld: '⏰'
    };
    return icons[module] || '❓';
  };
  // INTRO VIEW - Child-friendly with CITO branding
  if (view === 'intro') {
    return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 md:p-8 flex items-center justify-center" data-id="element-94">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-2xl" data-id="element-95">
          <Card className="overflow-hidden border-0 shadow-2xl ring-2 ring-indigo-100" data-id="element-96">
            {/* Distinctive CITO Header */}
            <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 p-8 md:p-10 text-white relative overflow-hidden" data-id="element-97">
              <div className="absolute inset-0 opacity-10" data-id="element-98">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" data-id="element-99" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" data-id="element-100" />
              </div>

              <div className="relative z-10" data-id="element-101">
                <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-bold mb-4 backdrop-blur-sm border border-white/30" data-id="element-102">
                  <Trophy className="w-4 h-4" data-id="element-103" />
                  <span data-id="element-104">CITO Oefentoets</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3" data-id="element-105">
                  Laat zien wat je al kunt!
                </h1>
                <p className="text-indigo-100 text-lg leading-relaxed max-w-lg" data-id="element-106">
                  Je gaat 30 vragen maken over rekenen, taal en spelling. Daarna
                  zie je precies wat je al goed kunt.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-6" data-id="element-107">
              {/* What to expect */}
              <div data-id="element-108">
                <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-109">
                  Wat kun je verwachten?
                </h2>
                <div className="grid gap-3" data-id="element-110">
                  <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-111">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-112">
                      <Brain className="w-5 h-5 text-white" data-id="element-113" />
                    </div>
                    <div data-id="element-114">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-115">
                        30 vragen
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-116">
                        Over rekenen, taal en spelling. Sommige makkelijk,
                        sommige wat moeilijker.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-violet-50 rounded-xl border border-violet-100" data-id="element-117">
                    <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-118">
                      <Clock className="w-5 h-5 text-white" data-id="element-119" />
                    </div>
                    <div data-id="element-120">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-121">
                        Ongeveer 30 minuten
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-122">
                        Je hoeft niet te haasten. Neem rustig de tijd om na te
                        denken.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-123">
                    <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-124">
                      <Star className="w-5 h-5 text-white" data-id="element-125" />
                    </div>
                    <div data-id="element-126">
                      <h3 className="font-semibold text-slate-900 mb-1" data-id="element-127">
                        Daarna zie je je resultaten
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-128">
                        We laten zien wat je al goed kunt en waar je nog kunt
                        oefenen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important to know */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5" data-id="element-129">
                <div className="flex gap-3" data-id="element-130">
                  <Heart className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-131" />
                  <div data-id="element-132">
                    <h3 className="font-bold text-amber-900 mb-2" data-id="element-133">
                      Belangrijk om te weten:
                    </h3>
                    <ul className="space-y-2 text-sm text-amber-800" data-id="element-134">
                      <li className="flex items-start gap-2" data-id="element-135">
                        <span className="text-amber-500 mt-1" data-id="element-136">✓</span>
                        <span data-id="element-137">
                          Je kunt niet terug naar een vorige vraag. Dat is niet
                          erg!
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-138">
                        <span className="text-amber-500 mt-1" data-id="element-139">✓</span>
                        <span data-id="element-140">
                          We kijken naar wat je <strong data-id="element-141">al goed</strong> kunt,
                          niet naar fouten.
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-142">
                        <span className="text-amber-500 mt-1" data-id="element-143">✓</span>
                        <span data-id="element-144">
                          Gebruik geen rekenmachine. Kladpapier mag wel!
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-145">
                        <span className="text-amber-500 mt-1" data-id="element-146">✓</span>
                        <span data-id="element-147">
                          Als je iets niet weet, kies dan het antwoord dat het
                          beste lijkt.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Understanding checkbox */}
              <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-200" data-id="element-148">
                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-149">
                  <input type="checkbox" checked={understandChecked} onChange={e => setUnderstandChecked(e.target.checked)} className="w-5 h-5 rounded border-2 border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 mt-0.5 cursor-pointer" data-id="element-150" />
                  <span className="text-slate-700 font-medium group-hover:text-slate-900" data-id="element-151">
                    Ik snap het en ben klaar om te beginnen
                  </span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2" data-id="element-152">
                <Button onClick={handleStartTest} disabled={!understandChecked} variant="primary" size="lg" className="flex-1 text-lg h-14 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-id="element-153">
                  <Sparkles className="w-5 h-5 mr-2" data-id="element-154" />
                  Start de toets
                </Button>
                <Button onClick={onExit} variant="secondary" size="lg" className="h-14" data-id="element-155">
                  Terug
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>;
  }
  // TEST VIEW - Clean, focused, with debug overlay
  if (view === 'test') {
    const progress = (currentQuestionIndex + 1) / citoQuestions.length * 100;
    const timeStatus = getTimeStatus();
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex flex-col" data-id="element-156">
        {/* Distinctive CITO Header */}
        <header className="bg-white border-b-2 border-indigo-200 sticky top-0 z-10 shadow-md h-12" data-id="element-157">
          <div className="max-w-4xl mx-auto px-4 h-full flex items-center justify-between" data-id="element-158">
            <div className="flex items-center gap-4" data-id="element-159">
              <div className="text-slate-600 text-sm font-medium" data-id="element-160">
                Vraag{' '}
                <span className="text-indigo-600 font-bold text-base" data-id="element-161">
                  {currentQuestionIndex + 1}
                </span>{' '}
                van {citoQuestions.length}
              </div>
            </div>

            {/* Gentle time indicator */}
            <div className="flex items-center gap-3" data-id="element-162">
              {timeStatus === 'ahead' && <span className="text-emerald-600 font-medium text-xs md:text-sm hidden sm:inline" data-id="element-163">
                  Je zit goed op schema
                </span>}
              {timeStatus === 'ontrack' && <span className="text-indigo-600 font-medium text-xs md:text-sm hidden sm:inline" data-id="element-164">
                  Je zit op schema
                </span>}
              {timeStatus === 'behind' && <span className="text-slate-600 font-medium text-xs md:text-sm" data-id="element-165">
                  Nog {formatTime(timeRemaining)}
                </span>}

              {/* Debug toggle */}
              <button onClick={() => setShowDebugInfo(!showDebugInfo)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title="Toggle debug info (Ctrl+D)" data-id="element-166">
                <Code className={`w-4 h-4 ${showDebugInfo ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-167" />
              </button>
            </div>
          </div>

          {/* CITO-branded progress bar */}
          <div className="h-1.5 bg-slate-100 w-full absolute bottom-0 left-0" data-id="element-168">
            <motion.div className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-500" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} data-id="element-169" />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-3xl w-full mx-auto p-3 md:p-6 flex flex-col justify-center" data-id="element-170">
          <AnimatePresence mode="wait" data-id="element-171">
            <motion.div key={currentQuestionIndex} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-172">
              <Card className="p-4 md:p-8 shadow-lg border-0 bg-white ring-2 ring-indigo-100 relative" data-id="element-173">
                {/* Debug Overlay */}
                {showDebugInfo && <div className="absolute top-4 right-4 bg-slate-900 text-white px-3 py-2 rounded-lg text-xs font-mono space-y-1 shadow-xl z-10" data-id="element-174">
                    <div className="flex items-center gap-2" data-id="element-175">
                      <span className="text-slate-400" data-id="element-176">Module:</span>
                      <span className="font-bold" data-id="element-177">
                        {getModuleIcon(currentQuestion.module)}{' '}
                        {currentQuestion.module}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-178">
                      <span className="text-slate-400" data-id="element-179">Niveau:</span>
                      <span className={`font-bold ${currentQuestion.difficulty === 1 ? 'text-green-400' : currentQuestion.difficulty === 2 ? 'text-yellow-400' : 'text-red-400'}`} data-id="element-180">
                        L{currentQuestion.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-181">
                      <span className="text-slate-400" data-id="element-182">ID:</span>
                      <span className="text-blue-400" data-id="element-183">
                        {currentQuestion.id}
                      </span>
                    </div>
                    {currentQuestion.subSkill && <div className="flex items-center gap-2" data-id="element-184">
                        <span className="text-slate-400" data-id="element-185">Skill:</span>
                        <span className="text-purple-400" data-id="element-186">
                          {currentQuestion.subSkill}
                        </span>
                      </div>}
                  </div>}

                {/* Context - Collapsible */}
                {currentQuestion.context && <div className="mb-4 bg-indigo-50 rounded-xl border border-indigo-100 overflow-hidden" data-id="element-187">
                    <button onClick={() => setIsContextExpanded(!isContextExpanded)} className="w-full flex items-center justify-between p-3 text-left hover:bg-indigo-100/50 transition-colors" data-id="element-188">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-189">
                        Informatie bij de vraag
                      </span>
                      {isContextExpanded ? <ChevronUp className="w-4 h-4 text-indigo-400" data-id="element-190" /> : <ChevronDown className="w-4 h-4 text-indigo-400" data-id="element-191" />}
                    </button>

                    <AnimatePresence data-id="element-192">
                      {isContextExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-193">
                          <div className="px-4 pb-4 pt-0 text-slate-700 text-base leading-relaxed border-t border-indigo-100/50 mt-1" data-id="element-194">
                            <LatexRenderer content={currentQuestion.context} data-id="element-195" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    {!isContextExpanded && <div className="px-4 pb-3 text-slate-500 text-sm truncate italic" data-id="element-196">
                        Klik om de informatie te lezen...
                      </div>}
                  </div>}

                {/* Question */}
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-slate-900 mb-6 leading-relaxed" data-id="element-197">
                  <LatexRenderer content={currentQuestion.question} data-id="element-198" />
                </h2>

                {/* Options with keyboard hints */}
                <div className="space-y-2.5" data-id="element-199">
                  {currentQuestion.options.map((option, index) => <button key={index} onClick={() => handleAnswerSelect(index)} className={`w-full text-left p-3 md:p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 md:gap-4 group ${selectedAnswer === index ? 'border-indigo-500 bg-indigo-50 shadow-md scale-[1.01] ring-2 ring-indigo-200' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50 hover:scale-[1.005]'}`} data-id="element-200">
                      <div className={`w-8 h-8 md:w-9 md:h-9 rounded-full border-2 flex items-center justify-center text-sm md:text-base font-bold transition-all flex-shrink-0 ${selectedAnswer === index ? 'border-indigo-500 bg-indigo-600 text-white shadow-lg' : 'border-slate-300 text-slate-400 group-hover:border-indigo-400 group-hover:text-indigo-500'}`} data-id="element-201">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="text-base md:text-lg text-slate-800 leading-relaxed flex-1" data-id="element-202">
                        <LatexRenderer content={option} data-id="element-203" />
                      </div>
                      {/* Keyboard hint - Hidden on mobile */}
                      <div className="hidden md:block text-xs text-slate-400 font-mono opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-204">
                        {index + 1}
                      </div>
                    </button>)}
                </div>

                {/* Keyboard shortcuts hint - Hidden on mobile */}
                <div className="hidden md:block mt-4 text-center text-xs text-slate-400" data-id="element-205">
                  Tip: Gebruik toetsen 1-4 of A-D om te kiezen, Enter om door te
                  gaan
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t-2 border-indigo-200 p-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20" data-id="element-206">
          <div className="max-w-3xl mx-auto flex justify-end" data-id="element-207">
            <Button onClick={handleNextQuestion} disabled={selectedAnswer === null} variant="primary" size="lg" className="w-full md:w-auto min-w-[180px] h-12 text-lg disabled:opacity-50 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md" data-id="element-208">
              {isLastQuestion ? 'Klaar!' : 'Volgende vraag'}
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-209" />
            </Button>
          </div>
        </footer>
      </div>;
  }
  // REVIEW VIEW - Positive, growth-focused with CITO branding
  if (view === 'review' && stats) {
    return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 md:p-8" data-id="element-210">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-211">
          {/* Positive Header */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-8" data-id="element-212">
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-bold mb-4 border border-indigo-200" data-id="element-213">
              <Trophy className="w-4 h-4" data-id="element-214" />
              <span data-id="element-215">CITO Oefentoets voltooid!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-216">
              Dit laat zien wat je al kunt
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto" data-id="element-217">
              Hieronder zie je precies waar je al goed in bent en waar je nog
              kunt oefenen.
            </p>

            {/* Completion XP Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold shadow-lg" data-id="element-218">
              <Sparkles className="w-5 h-5" data-id="element-219" />
              <span data-id="element-220">+100 XP voor voltooien!</span>
            </motion.div>
          </motion.div>

          {/* Main Score */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.1
        }} data-id="element-221">
            <Card className="p-8 bg-gradient-to-br from-white to-indigo-50 border-0 shadow-xl ring-2 ring-indigo-100" data-id="element-222">
              <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-223">
                {/* Score visualization */}
                <div className="relative w-40 h-40" data-id="element-224">
                  <svg className="w-full h-full transform -rotate-90" data-id="element-225">
                    <circle cx="50%" cy="50%" r="45%" fill="none" stroke="#e0e7ff" strokeWidth="12" data-id="element-226" />
                    <motion.circle cx="50%" cy="50%" r="45%" fill="none" stroke="url(#scoreGradient)" strokeWidth="12" strokeLinecap="round" initial={{
                    pathLength: 0
                  }} animate={{
                    pathLength: stats.percentage / 100
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeOut',
                    delay: 0.3
                  }} style={{
                    strokeDasharray: '283',
                    strokeDashoffset: '0'
                  }} data-id="element-227" />
                    <defs data-id="element-228">
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-229">
                        <stop offset="0%" stopColor="#6366f1" data-id="element-230" />
                        <stop offset="50%" stopColor="#8b5cf6" data-id="element-231" />
                        <stop offset="100%" stopColor="#a855f7" data-id="element-232" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-233">
                    <motion.span className="text-5xl font-bold text-slate-900" initial={{
                    opacity: 0,
                    scale: 0.5
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.6,
                    type: 'spring'
                  }} data-id="element-234">
                      {stats.correctCount}
                    </motion.span>
                    <span className="text-sm text-slate-500 font-medium" data-id="element-235">
                      van {answers.length}
                    </span>
                  </div>
                </div>

                {/* Positive message */}
                <div className="flex-1 text-center md:text-left" data-id="element-236">
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-bold mb-3 border border-indigo-200" data-id="element-237">
                    Niveau {stats.grade}
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-238">
                    {stats.gradeText}!
                  </h2>
                  <p className="text-slate-600 text-lg mb-4" data-id="element-239">
                    Je hebt{' '}
                    <span className="font-bold text-slate-900" data-id="element-240">
                      {stats.correctCount} vragen
                    </span>{' '}
                    goed beantwoord.
                    {stats.percentage >= 70 && ' Dat is knap!'}
                    {stats.percentage < 70 && stats.percentage >= 55 && ' Goed gedaan!'}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-241">
                    Dit niveau laat zien hoe ver je bent. Ook moeilijkere vragen
                    goed maken telt extra mee!
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Strengths & Growth areas */}
          {(stats.strengths.length > 0 || stats.weaknesses.length > 0) && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-242">
              {/* Strengths */}
              {stats.strengths.length > 0 && <Card className="p-6 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200" data-id="element-243">
                  <div className="flex items-center gap-3 mb-4" data-id="element-244">
                    <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-245">
                      <Star className="w-6 h-6 text-white" data-id="element-246" />
                    </div>
                    <div data-id="element-247">
                      <h3 className="font-bold text-slate-900 text-lg" data-id="element-248">
                        Waar je al goed in bent
                      </h3>
                      <p className="text-sm text-slate-600" data-id="element-249">
                        Dit kun je al super!
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2.5" data-id="element-250">
                    {stats.strengths.slice(0, 5).map(subSkill => <div key={subSkill} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-emerald-100" data-id="element-251">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-252" />
                        <span className="text-slate-700 font-medium" data-id="element-253">
                          {formatSubSkill(subSkill)}
                        </span>
                      </div>)}
                  </div>
                </Card>}

              {/* Growth areas */}
              {stats.weaknesses.length > 0 && <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-200" data-id="element-254">
                  <div className="flex items-center gap-3 mb-4" data-id="element-255">
                    <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg" data-id="element-256">
                      <Target className="w-6 h-6 text-white" data-id="element-257" />
                    </div>
                    <div data-id="element-258">
                      <h3 className="font-bold text-slate-900 text-lg" data-id="element-259">
                        Hier kun je nog oefenen
                      </h3>
                      <p className="text-sm text-slate-600" data-id="element-260">
                        Dit wordt nog beter!
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2.5" data-id="element-261">
                    {stats.weaknesses.slice(0, 5).map(subSkill => <div key={subSkill} className="flex items-center justify-between gap-3 p-3 bg-white rounded-xl border border-indigo-100" data-id="element-262">
                        <div className="flex items-center gap-3" data-id="element-263">
                          <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-264" />
                          <span className="text-slate-700 font-medium" data-id="element-265">
                            {formatSubSkill(subSkill)}
                          </span>
                        </div>
                        <Button variant="secondary" size="sm" onClick={() => alert('Oefenmodus voor dit onderwerp komt binnenkort!')} className="text-xs h-8 px-3 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200" data-id="element-266">
                          Oefen dit
                          <ArrowRight className="w-3 h-3 ml-1.5" data-id="element-267" />
                        </Button>
                      </div>)}
                  </div>
                </Card>}
            </motion.div>}

          {/* Detailed review */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-268">
            <Card className="p-6" data-id="element-269">
              <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2" data-id="element-270">
                <BarChart3 className="w-5 h-5 text-slate-400" data-id="element-271" />
                Alle vragen bekijken
              </h3>
              <div className="space-y-2" data-id="element-272">
                {answers.map((answer, idx) => <ReviewItem key={idx} answer={answer} index={idx} data-id="element-273" />)}
              </div>
            </Card>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex justify-center pt-4 pb-8" data-id="element-274">
            <Button onClick={onExit} variant="primary" size="lg" className="min-w-[240px] h-14 text-lg bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700" data-id="element-275">
              Terug naar Dashboard
            </Button>
          </motion.div>
        </div>
      </div>;
  }
  return null;
}
// Review Item component
function ReviewItem({
  answer,
  index
}: {
  answer: Answer;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const question = getQuestionWithExplanation(answer.questionId);
  if (!question) return null;
  const userAnswerText = question.options[answer.userChoice];
  const correctAnswerText = question.options[question.correctAnswer];
  return <div className={`rounded-xl border-2 transition-all ${answer.isCorrect ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-id="element-276">
      <div onClick={() => setIsExpanded(!isExpanded)} className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50/50" data-id="element-277">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${answer.isCorrect ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-278">
          {answer.isCorrect ? <CheckCircle className="w-5 h-5" data-id="element-279" /> : <span className="font-bold text-sm" data-id="element-280">{index + 1}</span>}
        </div>

        <div className="flex-1 min-w-0" data-id="element-281">
          <p className="text-slate-700 font-medium truncate" data-id="element-282">
            Vraag {index + 1}: <LatexRenderer content={question.question} data-id="element-283" />
          </p>
          {answer.subSkill && <p className="text-xs text-slate-500 capitalize mt-1" data-id="element-284">
              {formatSubSkill(answer.subSkill)}
            </p>}
        </div>

        <div className="text-slate-400" data-id="element-285">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-286" /> : <ChevronDown className="w-5 h-5" data-id="element-287" />}
        </div>
      </div>

      <AnimatePresence data-id="element-288">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-289">
            <div className="p-4 pt-0 space-y-4" data-id="element-290">
              {question.context && <div className="p-3 bg-white rounded-lg border border-slate-200 text-sm text-slate-600" data-id="element-291">
                  <LatexRenderer content={question.context} data-id="element-292" />
                </div>}

              <div className="text-base font-medium text-slate-900" data-id="element-293">
                <LatexRenderer content={question.question} data-id="element-294" />
              </div>

              <AnswerComparison userAnswer={userAnswerText} correctAnswer={correctAnswerText} isCorrect={answer.isCorrect} mode="compact" data-id="element-295" />

              <ExplanationCard explanation={question.explanation || ''} distractorExplanation={!answer.isCorrect && question.distractorExplanations ? question.distractorExplanations[answer.userChoice.toString()] || 'Helaas, dat was niet het juiste antwoord.' : undefined} isCorrect={answer.isCorrect} mode="compact" data-id="element-296" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}